<template>
  <div>
    <div class="map__container">
      <div class="map__loading">Loading...</div>
      <div id="map" class="map"></div>
    </div>
    <div v-if="currentTripId" class="current-trip-report">
      <v-row class="mt-2">
        <v-col cols="12">
          <v-btn v-show="currentTripId" icon small class="cursor-pointer float-right" @click="clearDataByEventId()">
            <v-icon>{{ mdiClose }}</v-icon>
          </v-btn>
          <h3>Viewing trip {{ currentTripId }}</h3>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <h3 class="mt-2 mb-3">Log Type Key:</h3>
          <v-row>
            <v-col v-for="logType in currentLogTypes" :key="logType" cols="4" class="d-flex align-center py-2">
              <span :class="`logTypeIcon logTypeIcon--${logType}`"></span>
              {{ logType }}
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <h3 class="mt-2 mb-2">Trip Report Details</h3>
            <v-simple-table dense>
              <thead>
                <tr>
                  <th colspan="2">Trip Details</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Trip ID</td>
                  <td>{{ currentTripData.trip_id }}</td>
                </tr>
                <tr>
                  <td>Vehicle ID</td>
                  <td>{{ currentTripData.vehicle_id }}</td>
                </tr>
                <tr>
                  <td>Start Time</td>
                  <td>{{ currentTripData.start_datetime }}</td>
                </tr>
                <tr>
                  <td>End Time</td>
                  <td>{{ currentTripData.end_datetime }}</td>
                </tr>
                <tr>
                  <td>Stops Expected</td>
                  <td>{{ currentTripData.stops_expected }}</td>
                </tr>
                <tr>
                  <td>Stops Announced</td>
                  <td>{{ currentTripData.stops_announced }}</td>
                </tr>
              </tbody>
              <thead>
                <tr>
                  <th colspan="2">Trip Logs</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(log, index) in currentTripData.logs" :key="index">
                  <td>{{ log.timestamp }}</td>
                  <td>
                    <span :class="`logTypeIcon logTypeIcon--${log.log_type}`"></span> {{ log.log_type }}
                    <span v-if="log.stop_id"> - Stop {{ log.stop_id }}</span>
                    <span v-if="log.stop_announcement_count > 1">
                      (Stop announced {{ log.stop_announcement_count }} times)
                    </span>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import mapboxgl from "mapbox-gl"
import "mapbox-gl/dist/mapbox-gl.css"
import { mdiClose, mdiConsoleLine } from "@mdi/js"

export default {
  name: "VehicleGPSHistoryMap",
  props: {
    gpsData: {
      type: Array,
      default: () => [],
      required: false,
    },
    dataByEventId: {
      type: Array,
      default: () => [],
      required: false,
    },
  },
  data() {
    return {
      map: null,
      currentMarkers: [],
      currentLogTypes: [],
    }
  },
  setup() {
    return {
      mdiClose,
    }
  },
  computed: {
    currentTripId() {
      return this.dataByEventId[0]?.event_data?.trip_id
    },

    currentTripData() {
      return this.dataByEventId[0].event_data
    },

    filteredGpsData() {
      if (this.currentTripId) {
        return this.gpsData.filter(data => decodeURIComponent(data.trip_id) === decodeURIComponent(this.currentTripId))
      }

      return this.gpsData
    },
  },
  watch: {
    dataByEventId() {
      this.drawEventMarkers()
    },

    gpsData() {
      if (this.map && this.map.isStyleLoaded()) {
        this.updateVehicleGPSLayer()
      }
    },
  },

  mounted() {
    mapboxgl.accessToken = this.$config.mapbox.accessToken
    this.map = new mapboxgl.Map({
      container: "map",
      style: this.$config.mapbox.style,
      center: [174.779, -41.28988],
      zoom: 14,
    })

    this.map.on("load", () => {
      // 1. Force a resize to fix modal rendering quirks
      setTimeout(() => {
        this.map.resize()
      }, 200)

      // 2. Draw the markers and routes if data was provided immediately
      if (this.dataByEventId && this.dataByEventId.length) {
        this.drawEventMarkers()
      }

      if (this.gpsData && this.gpsData.length) {
        this.updateVehicleGPSLayer()
      }
    })
  },

  beforeDestroy() {
    this.map.remove()
  },

  methods: {
    drawEventMarkers() {
      const features = []

      // remove current markers
      this.currentMarkers.forEach(marker => marker.remove())
      this.currentMarkers = []

      this.dataByEventId.map(data => {
        const points = data?.event_data.logs || []

        points.forEach(item => {
          const description = `
            <div class="mapboxgl-popup-content-desc">
              <p>Log Type: ${item.log_type}</p>
              <p>Stop ID: ${item.stop_id}</p>
              <p>Timestamp: ${item.timestamp}</p>
              <p>Location: ${item.location.lat}, ${item.location.lon}</p>
            </div>
          `

          if (!this.currentLogTypes.includes(item.log_type)) {
            this.currentLogTypes.push(item.log_type)
          }

          features.push({
            type: "Feature",
            geometry: {
              type: "Point",
              coordinates: [item.location.lon, item.location.lat],
            },
            properties: {
              title: item.log_type,
              className: item.log_type,
              description,
              iconSize: [15, 15],
            },
          })
        })

        return features
      })

      const geoJson = {
        type: "FeatureCollection",
        features,
      }

      geoJson.features.forEach(marker => {
        this.createSquareMarker(marker)
      })

      if (this.map && this.map.isStyleLoaded()) {
        this.updateVehicleGPSLayer()
      }
    },

    createSquareMarker({ geometry: { coordinates }, properties: { className, title, description, color, iconSize } }) {
      const el = document.createElement("div")
      const icon = document.createElement("div")
      icon.className = `logTypeIcon logTypeIcon--${className}`
      el.appendChild(icon)

      const marker = new mapboxgl.Marker(el)
        .setLngLat(coordinates)
        .setPopup(
          new mapboxgl.Popup({ offset: 25 }) // add popups
            .setHTML(description),
        )
        .addTo(this.map)

      this.currentMarkers.push(marker)
    },

    clearDataByEventId() {
      this.currentMarkers.forEach(marker => marker.remove())
      this.currentLogTypes = []
      this.$emit("update:dataByEventId", [])
    },

    updateVehicleGPSLayer() {
      const routeSourceId = "route"
      const offrouteSourceId = "offRoutes"
      const routeLayerId = "route"
      const offroutesLayerId = "OffRoutes"
      const directionsLayerId = "directions"

      const featureLineString = []
      const featureCollectionOffRoutes = []
      const offRoutes = []
      const GPSTraces = this.filteredGpsData

      if (this.map.getLayer(routeLayerId)) {
        this.map.removeLayer(routeLayerId)
      }
      if (this.map.getLayer(offroutesLayerId)) {
        this.map.removeLayer(offroutesLayerId)
      }
      if (this.map.getLayer(directionsLayerId)) {
        this.map.removeLayer(directionsLayerId)
      }

      // Create a "LngLatBounds" with both corners at the first coordinate.
      const bounds = this.gpsData.length
        ? new mapboxgl.LngLatBounds(
            [GPSTraces[0].longitude, GPSTraces[0].latitude],
            [GPSTraces[0].longitude, GPSTraces[0].latitude],
          )
        : null

      GPSTraces.forEach((GPSTrace, index) => {
        if (GPSTrace.latitude !== 0 && GPSTrace.longitude !== 0) {
          featureLineString.push([GPSTrace.longitude, GPSTrace.latitude])

          // Extend the "LngLatBounds" to include every 5th coordinate in the bounds result.
          if (index % 5 === 0) {
            bounds.extend([GPSTrace.longitude, GPSTrace.latitude])
          }
        }
      })

      offRoutes.forEach(offRoute => {
        featureCollectionOffRoutes.push({
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: [offRoute.long, offRoute.lat],
          },
          properties: {
            trip_id: offRoute.trip_id,
          },
        })
      })

      if (this.map.getSource(routeSourceId)) {
        this.map.removeSource(routeSourceId)
      }
      if (this.map.getSource(offrouteSourceId)) {
        this.map.removeSource(offrouteSourceId)
      }

      this.map.addSource(routeSourceId, {
        type: "geojson",
        data: {
          type: "Feature",
          properties: {},
          geometry: {
            type: "LineString",
            coordinates: featureLineString,
          },
        },
      })

      this.map.addSource(offrouteSourceId, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: featureCollectionOffRoutes,
        },
      })

      this.map.addLayer({
        id: routeLayerId,
        type: "line",
        source: "route",
        layout: {
          "line-join": "round",
          "line-cap": "round",
        },
        paint: {
          "line-color": "#ec343a",
          "line-width": 1,
        },
      })

      this.map.addLayer({
        id: offroutesLayerId,
        type: "symbol",
        source: "offRoutes",
        layout: {
          visibility: "visible",
          "icon-image": "poi-marker",
          "icon-allow-overlap": true,
          "text-field": "{trip_id}",
          "text-allow-overlap": true,
          "text-ignore-placement": true,
          "text-size": 11,
          "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
          "text-offset": [0, 0.6],
          "text-anchor": "top",
        },
        paint: {
          "text-color": "#aa0000",
        },
      })

      this.map.addLayer({
        id: directionsLayerId,
        type: "symbol",
        source: "route",
        layout: {
          "symbol-placement": "line",
          "symbol-spacing": 75,
          "text-field": "➤",
          "text-size": 18,
          "text-keep-upright": false,
          "text-allow-overlap": true,
          "text-ignore-placement": true,
        },
        paint: {
          "text-color": "#ec343a",
          "text-halo-color": "#ffffff",
          "text-halo-width": 1,
        },
      })

      if (bounds) {
        this.map.fitBounds(bounds, {
          padding: 50,
        })
      }
    },
  },
}
</script>

<style lang="scss">
$popup-padding: 11px;
$color-red: #ff4100;
$tip-color: #ffffff;
$popup-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);

.map {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 100%;

  &__container {
    margin: 0;
    padding: 0;
    height: 25rem;
    position: relative;
  }
}

.logTypeIcon {
  display: block;
  float: left;
  width: 1rem;
  height: 1rem;
  margin-right: 5px;
  background-color: #aaaaaa;

  &--trip_start {
    background-color: #00a740;
    rotate: 45deg;
  }
  &--trip_end {
    background-color: #a70000;
    rotate: 45deg;
  }
  &--on_route {
    background-color: #d80086;
    border: 2px solid #000;
  }
  &--off_route {
    background-color: #d80086;
  }
  &--campaign_shown {
    background-color: #F39C12;
  }
  &--campaign_hidden {
    background-color: #F39C12;
    border: 2px solid #000;
  }
  &--stop_announced {
    background-color: #456df2;
    border-radius:100%;
  }
  &--stop_announcement_missed {
    background-color: #ee8787;
    border-radius:100%;
  }
  &--stop_passed {
    background-color: #002a3a;
    border-radius:100%;
  }
  &--stop_skipped {
    background-color: #5d636e;
    border-radius:100%;
  }
}

.mapboxgl-popup {
  .mapboxgl-popup-content {
    border-radius: 10px;
    padding: $popup-padding;
    box-shadow: $popup-shadow;
    background-color: $tip-color;

    &-desc {
      font-size: 0.75rem;
      font-weight: 500;
      line-height: 21px;

      p {
        margin: 0;
      }
    }
  }

  &-close-button {
    width: 20px;
    border: none;
    border-radius: 0 10px 0 10px;
    font-size: 20px;
    padding: 5px;

    &:hover {
      background-color: $color-red;
      color: white;
    }
  }
}

.mapboxgl-popup-anchor-top .mapboxgl-popup-tip {
  border-bottom-color: $tip-color;
}

.mapboxgl-popup-anchor-top-left .mapboxgl-popup-tip {
  border-bottom-color: $tip-color;
}

.mapboxgl-popup-anchor-top-right .mapboxgl-popup-tip {
  border-bottom-color: $tip-color;
}

.mapboxgl-popup-anchor-bottom .mapboxgl-popup-tip {
  border-top-color: $tip-color;
}

.mapboxgl-popup-anchor-bottom-left .mapboxgl-popup-tip {
  border-top-color: $tip-color;
}

.mapboxgl-popup-anchor-bottom-right .mapboxgl-popup-tip {
  border-top-color: $tip-color;
}

.mapboxgl-popup-anchor-left .mapboxgl-popup-tip {
  border-right-color: $tip-color;
}

.mapboxgl-popup-anchor-right .mapboxgl-popup-tip {
  border-left-color: $tip-color;
}
</style>
